import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AddBusinessOutlined, CloseOutlined } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { tenantsApi } from '@/api/tenants.api';
import { useNotify } from '@/components/common/useNotify';
import FormErrorSummary from '@/components/common/FormErrorSummary';
import SubmitButton from '@/components/common/SubmitButton';
import type { RegisterTenantRequest } from '@/types/auth';

interface FormValues extends RegisterTenantRequest {
  provisioningKey: string;
}

/** Provision a new tenant — POST /api/tenants (X-Provisioning-Key header). */
export default function ProvisionTenantDialog({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => void }) {
  const notify = useNotify();
  const fullScreen = useMediaQuery(useTheme().breakpoints.down('sm'));
  const { control, handleSubmit, reset, formState } = useForm<FormValues>({
    defaultValues: {
      provisioningKey: '', companyName: '', legalName: '', email: '', phone: '',
      adminFirstName: '', adminLastName: '', adminEmail: '',
    },
  });

  const mutation = useMutation({
    mutationFn: (v: FormValues) => {
      const { provisioningKey, ...body } = v;
      return tenantsApi.provision(
        { ...body, legalName: body.legalName || null, phone: body.phone || null },
        provisioningKey,
      );
    },
    onSuccess: () => {
      notify.success('Company created.');
      reset();
      onDone();
      onClose();
    },
    onError: (e) => notify.error(e),
  });

  const text = (name: keyof FormValues, label: string, type = 'text', required = true) => (
    <Grid size={{ xs: 12, sm: 6 }} key={name}>
      <Controller name={name} control={control} rules={required ? { required: 'Required' } : undefined} render={({ field, fieldState }) => (
        <TextField {...field} value={(field.value as string) ?? ''} label={label} type={type} fullWidth size="small" error={!!fieldState.error} helperText={fieldState.error?.message} />
      )} />
    </Grid>
  );

  return (
    <Dialog open={open} onClose={mutation.isPending ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <AddBusinessOutlined color="primary" fontSize="small" />
          <span>Create Company</span>
        </Stack>
        <IconButton aria-label="Close" onClick={onClose} disabled={mutation.isPending} size="small" sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseOutlined fontSize="small" />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))} noValidate style={{ display: 'contents' }}>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2, py: 0.25 }}>
            Requires the server provisioning key (control-plane operation).
          </Alert>
          <FormErrorSummary errors={formState.errors} title="Fill in the required fields." />
          <Grid container spacing={2}>
            <Grid size={12}>
              <Controller name="provisioningKey" control={control} rules={{ required: 'Required' }} render={({ field, fieldState }) => (
                <TextField {...field} label="Provisioning key" type="password" fullWidth size="small" error={!!fieldState.error} helperText={fieldState.error?.message} />
              )} />
            </Grid>
            <Grid size={12}><SectionLabel>Company</SectionLabel></Grid>
            {text('companyName', 'Company name')}
            {text('legalName', 'Legal name', 'text', false)}
            {text('email', 'Company email')}
            {text('phone', 'Phone', 'text', false)}
            <Grid size={12}><SectionLabel>Administrator</SectionLabel></Grid>
            {text('adminFirstName', 'First name')}
            {text('adminLastName', 'Last name')}
            {text('adminEmail', 'Admin email')}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button color="inherit" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <SubmitButton loading={mutation.isPending} loadingText="Provisioning…">Provision</SubmitButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ display: 'block', lineHeight: 1.6, fontWeight: 700, borderBottom: 1, borderColor: 'divider', pb: 0.25 }}
    >
      {children}
    </Typography>
  );
}
